import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firestore-DB-config/firebase-config";
import {
  Button,
  TextField,
  Container,
  Typography,
  Box,
  Snackbar,
  SnackbarContent,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

interface FormState {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState<FormState>({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false); // New loading state
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true); // Set loading to true when starting the login process
    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate("/manage-your-content");
    } catch {
      setError("Login failed. Please check your credentials.");
      setSnackbarOpen(true);
    } finally {
      setLoading(false); // Set loading to false after the process is complete
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <>
      <Header />
      <Container
        sx={{
          my: { xs: 10, md: 20 },
          width: { xs: "90%", sm: "80%", md: "500px" }, // Responsive width
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            p: { xs: 3, sm: 4 },
            bgcolor: "background.paper",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "primary.main",
              fontSize: { xs: "24px", sm: "28px", md: "32px" },
            }} // Responsive font size
          >
            Login
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 3,
              color: "text.secondary",
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            Enter your credentials to manage the content.
          </Typography>
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              fullWidth
              InputProps={{
                sx: { borderRadius: "12px" },
              }}
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              required
              variant="outlined"
              fullWidth
              InputProps={{
                sx: { borderRadius: "12px" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: { xs: "14px", sm: "16px" }, // Responsive font size for button
              }}
              disabled={loading} // Disable the button when loading
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Login"
              )}
            </Button>
          </Box>
        </Box>
      </Container>
      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <SnackbarContent
          message={error}
          sx={{
            backgroundColor: "#d32f2f", // Dark red for error
            color: "white",
            borderRadius: "8px",
            padding: "16px",
            fontWeight: "500",
          }}
          action={
            <Button color="inherit" onClick={handleCloseSnackbar}>
              <CloseRoundedIcon />
            </Button>
          }
        />
      </Snackbar>
    </>
  );
};

export default Login;
